import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useConfig } from '../config';
import { isOrganizationAdminOrOwner, isOrganizationBillingManagerOrOwner, isOrganizationManager, OrganizationRole } from '../models/organizationRole';
import { resolvePath, sso as ssoPath, useQuery } from '../routes';
import {
  fromAuth,
  fromOrganizationUsers,
  fromSubscriptions,
  fromTestResultCount,
  fromTestOpsProject,
  fromOrganizations,
  fromOrganizationFeatureFlag,
} from '../store';
import MenuListDecorator from './MenuListDecorator';
import {
  accountSubscriptionManagement,
  dashboard,
  licenseUtilization,
  maintenanceProcess,
  organizationManagement,
  paymentMethod,
  projectManagement,
  supportManagement,
  userManagement,
} from './routes';
import {
  LicenseUtilizationCircleIcon,
  LicenseManagementCircleIcon,
  UserManagementCircleIcon,
  PaymentMethodCircleIcon,
  SubscriptionManagementCircleIcon,
  OrganizationManagementCircleIcon,
  TeamManagementCircleIcon,
  ProjectManagementCircleIcon,
  TestOpsHomepageCircleIcon,
  SupportManagementCircleIcon,
} from './CustomIcon';
import { useLaunchDarkly } from '../launchdarkly';
import { OrganizationMigrationStatus, UsagePlan } from '../models';

const useStyles = makeStyles(theme => ({
  settingButton: {
    height: '100%',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  settingIcon: {
    cursor: 'pointer',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  menuItem: {
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
  },
  title: {
    '&.Mui-disabled': {
      opacity: 1,
    },
  },
  textTitle: {
    paddingLeft: theme.spacing(1),
  },
  subtitle: {
    textTransform: 'uppercase',
    paddingLeft: theme.spacing(1.5),
    '&.Mui-disabled': {
      opacity: 1,
    },
  },
  itemIcon: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(1),
  },
  menuContainer: {
    maxHeight: '90vh',
    overflow: 'auto',
  },
  primaryTitle: {
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const OrganizationSettingMenu = () => {
  const classes = useStyles();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const orgId = Number(useQuery().get('orgId'));
  const { config } = useConfig();
  const { queryDictionary } = useQuery();
  const user = useSelector(fromAuth.selectUser);
  const isRootOrAdmin = useSelector(fromAuth.selectIsRootOrAdmin);
  const { flags } = useLaunchDarkly();
  const testResultCount = useSelector(fromTestResultCount.selectLatestTestResultCount);
  const currentOrgUser = useSelector(fromOrganizationUsers.selectByUserEmailAndOrganizationId(
    user?.email || '',
    orgId,
  ));
  const projectCountByOrgId = useSelector(fromTestOpsProject.selectProjectCount);
  const hasTOEnterprise = useSelector(fromSubscriptions
    .selectHasTOEnterpriseByOrganizationId(orgId || 0));

  const hasTOPlatformUltimate = useSelector(fromSubscriptions
    .selectHasTOPlatformByOrganizationId(orgId || 0));

  const isSsoFlagEnabled = useSelector(fromOrganizationFeatureFlag.selectIsOrganizationSsoEnabled(
    Number(orgId),
  )) || false;

  const hasEnableSSOAndCustomDomain = hasTOPlatformUltimate || hasTOEnterprise || isSsoFlagEnabled;

  const orgMigrationStatus = useSelector(fromOrganizations.selectOrgMigrationStatusById(orgId));

  const handleClick = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      // Triggered by clickaway listener, but what we clicked is the avatar
      // so we let this event be handled by handleClick()
      return;
    }
    setMenuOpen(false);
  };
  if (!currentOrgUser
    || (currentOrgUser.role === OrganizationRole.MEMBER && !isRootOrAdmin)) return <></>;

  const canAccessSubscriptionAndPaymentInfo = (
    isRootOrAdmin || isOrganizationBillingManagerOrOwner(currentOrgUser!!.role)
  ) && !config?.onpremise
    && ((flags && flags.subscriptionAndPaymentMethodDisabled === false) || flags === undefined);

  const getSubdomainLink = (domain: string | undefined) => `https://${domain}.${process.env.REACT_APP_CUSTOM_DOMAIN_POSTFIX}`;

  const goToProjects = () => {
    let projectUri: string;
    const oldUI = resolvePath(ssoPath, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/teams/projects` });
    const newUI = resolvePath(projectManagement, undefined, { orgId: `${orgId}` });
    const maintenanceUI = resolvePath(maintenanceProcess, undefined);
    if (orgMigrationStatus === OrganizationMigrationStatus.MIGRATED) {
      projectUri = newUI;
    } else if (flags?.projectStandardizeEnabled) {
      projectUri = maintenanceUI;
    } else if (flags?.projectManagementNewUIEnabled) {
      projectUri = newUI;
    } else {
      projectUri = oldUI;
    }
    return projectUri;
  };

  return (
    <div id="organization_setting">
      <IconButton
        onClick={handleClick}
        ref={anchorRef}
        className={classes.settingButton}
        size="large"
      >
        <Avatar
          className={classes.settingIcon}
        >
          <SettingsOutlinedIcon />
        </Avatar>
      </IconButton>
      <MenuListDecorator
        open={menuOpen}
        anchorEl={anchorRef.current}
        onClickAway={closeMenu}
      >
        <div className={classes.menuContainer}>
          <MenuItem
            disabled
            classes={{ root: classes.title }}
          >
            <Typography variant="h3" className={classes.textTitle}>
              <FormattedMessage id="layout.header.organizationsetting.settings" />
            </Typography>
          </MenuItem>
          <MenuItem
            disabled
            className={classes.subtitle}
          >
            <Typography variant="subtitle1" className={classes.subtitle}>
              <FormattedMessage id="layout.header.organizationsetting.testopsadmin" />
            </Typography>
          </MenuItem>
          {(isRootOrAdmin || isOrganizationManager(currentOrgUser!!.role)) && (
            <MenuItem
              classes={{ root: classes.menuItem }}
              onClick={closeMenu}
              component={RouterLink}
              to={resolvePath(
                currentOrgUser!!.role === OrganizationRole.BILLING_MANAGER
                  ? dashboard : licenseUtilization,
                undefined,
                { orgId: `${orgId}` },
              )}
            >
              <ListItemAvatar className={classes.itemIcon}>
                <LicenseUtilizationCircleIcon />
              </ListItemAvatar>
              <ListItemText
                primary={(
                  <FormattedMessage id="layout.header.organizationsetting.license_utilization" />
                )}
                secondary={(
                  <FormattedMessage id="layout.header.organizationsetting.license_utilization_detail" />
                )}
              />
            </MenuItem>
          )}
          <MenuItem
            classes={{ root: classes.menuItem }}
            onClick={closeMenu}
            component={RouterLink}
            to={resolvePath(ssoPath, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/admin/license_keys/kse` })}
          >
            <ListItemAvatar className={classes.itemIcon}>
              <LicenseManagementCircleIcon />
            </ListItemAvatar>
            <ListItemText
              primary={(
                <FormattedMessage id="layout.header.organizationsetting.license_management" />
              )}
              secondary={(
                <FormattedMessage id="layout.header.organizationsetting.license_management_detail" />
              )}
            />
          </MenuItem>
          {(isRootOrAdmin || isOrganizationAdminOrOwner(currentOrgUser!!.role)) && (
            <MenuItem
              classes={{ root: classes.menuItem }}
              onClick={closeMenu}
              component={RouterLink}
              to={resolvePath(userManagement, undefined, { orgId: `${orgId}` })}
            >
              <ListItemAvatar className={classes.itemIcon}>
                <UserManagementCircleIcon />
              </ListItemAvatar>
              <ListItemText
                primary={(
                  <FormattedMessage id="layout.header.organizationsetting.user_management" />
                )}
                secondary={(
                  <FormattedMessage id="layout.header.organizationsetting.user_management_detail" />
                )}
              />
            </MenuItem>
          )}
          {canAccessSubscriptionAndPaymentInfo && (
            <>
              <MenuItem
                classes={{ root: classes.menuItem }}
                onClick={closeMenu}
                component={RouterLink}
                to={resolvePath(
                  paymentMethod,
                  undefined,
                  { orgId, accountId: currentOrgUser.organization.accountId },
                )}
              >
                <ListItemAvatar className={classes.itemIcon}>
                  <PaymentMethodCircleIcon />
                </ListItemAvatar>
                <ListItemText
                  primary={(
                    <FormattedMessage id="layout.header.organizationsetting.payment_method" />
                  )}
                  secondary={(
                    <FormattedMessage id="layout.header.organizationsetting.payment_method_detail" />
                  )}
                />
              </MenuItem>
              <MenuItem
                classes={{ root: classes.menuItem }}
                onClick={closeMenu}
                component={RouterLink}
                to={resolvePath(
                  accountSubscriptionManagement,
                  undefined,
                  { orgId, accountId: currentOrgUser.organization.accountId },
                )}
              >
                <ListItemAvatar className={classes.itemIcon}>
                  <SubscriptionManagementCircleIcon />
                </ListItemAvatar>
                <ListItemText
                  primary={(
                    <FormattedMessage id="layout.header.organizationsetting.subscription_management" />
                  )}
                  secondary={(
                    <FormattedMessage id="layout.header.organizationsetting.subscription_management_detail" />
                  )}
                />
              </MenuItem>
            </>
          )}
          {(isRootOrAdmin || isOrganizationAdminOrOwner(currentOrgUser!!.role)) && (
            <MenuItem
              classes={{ root: classes.menuItem }}
              onClick={closeMenu}
              component={RouterLink}
              to={resolvePath(
                organizationManagement,
                undefined,
                { orgId: Number(orgId), accountId: currentOrgUser?.organization.accountId },
              )}
            >
              <ListItemAvatar className={classes.itemIcon}>
                <OrganizationManagementCircleIcon />
              </ListItemAvatar>
              <ListItemText
                primary={(
                  <FormattedMessage id="layout.header.organizationsetting.organization_management" />
                )}
                secondary={(
                  <FormattedMessage id="layout.header.organizationsetting.organization_management_detail" />
                )}
              />
            </MenuItem>
          )}
          {(isRootOrAdmin || isOrganizationAdminOrOwner(currentOrgUser!!.role))
            && !config?.onpremise && config?.serviceCloudEnabled && (
              <MenuItem
                classes={{ root: classes.menuItem }}
                onClick={closeMenu}
                component={RouterLink}
                to={resolvePath(supportManagement, undefined, queryDictionary())}
              >
                <ListItemAvatar className={classes.itemIcon}>
                  <SupportManagementCircleIcon />
                </ListItemAvatar>
                <ListItemText
                  primary={(
                    <Typography className={classes.primaryTitle}>
                      <FormattedMessage id="layout.header.organizationsetting.support_management" />
                    </Typography>
                  )}
                  secondary={(
                    <FormattedMessage id="layout.header.organizationsetting.support_management_detail" />
                  )}
                />
              </MenuItem>
          )}
          {(!config?.onpremise
            || (config.onpremise && testResultCount?.usagePlan !== UsagePlan.COMMUNITY)) && (
            <>
              <MenuItem
                disabled
                className={classes.subtitle}
              >
                <Typography variant="subtitle1" className={classes.subtitle}>
                  <FormattedMessage id="layout.header.organizationsetting.testopssetting" />
                </Typography>
              </MenuItem>

              { (orgMigrationStatus === undefined
                || orgMigrationStatus !== OrganizationMigrationStatus.MIGRATED)
                && (
                  <MenuItem
                    classes={{ root: classes.menuItem }}
                    onClick={closeMenu}
                    component={RouterLink}
                    to={
                      flags?.projectStandardizeEnabled
                        ? resolvePath(
                          maintenanceProcess,
                          undefined,
                        )
                        : resolvePath(
                          ssoPath,
                          undefined,
                          { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/teams/settings` },
                        )
                    }
                  >
                    <ListItemAvatar className={classes.itemIcon}>
                      <TeamManagementCircleIcon />
                    </ListItemAvatar>
                    <ListItemText
                      primary={(
                        <Typography className={classes.primaryTitle}>
                          <FormattedMessage id="layout.header.organizationsetting.team_management" />
                        </Typography>
                      )}
                      secondary={(
                        <FormattedMessage id="layout.header.organizationsetting.team_management_detail" />
                      )}
                    />
                  </MenuItem>
                )}

              {(isRootOrAdmin
                || isOrganizationAdminOrOwner(currentOrgUser!!.role)
                || (projectCountByOrgId[Number(orgId)] > 0
                  && currentOrgUser?.role === OrganizationRole.BILLING_MANAGER)
              ) && (
              <MenuItem
                classes={{ root: classes.menuItem }}
                onClick={closeMenu}
                component={RouterLink}
                to={goToProjects()}
              >
                <ListItemAvatar className={classes.itemIcon}>
                  <ProjectManagementCircleIcon />
                </ListItemAvatar>
                <ListItemText
                  primary={(
                    <Typography className={classes.primaryTitle}>
                      <FormattedMessage id="layout.header.organizationsetting.project_management" />
                    </Typography>
                  )}
                  secondary={(
                    <FormattedMessage id="layout.header.organizationsetting.project_management_detail" />
                  )}
                />
              </MenuItem>
              )}

              <MenuItem
                classes={{ root: classes.menuItem }}
                onClick={closeMenu}
                component={RouterLink}
                to={currentOrgUser?.organization?.domain && hasEnableSSOAndCustomDomain
                  ? resolvePath(ssoPath, undefined, { redirect_uri: `${getSubdomainLink(currentOrgUser?.organization?.domain)}/organization/${orgId}/home` })
                  : resolvePath(ssoPath, undefined, { redirect_uri: `${config?.testOpsPublicUrl}/organization/${orgId}/home` })}
              >
                <ListItemAvatar className={classes.itemIcon}>
                  <TestOpsHomepageCircleIcon />
                </ListItemAvatar>
                <ListItemText
                  primary={(
                    <Typography className={classes.primaryTitle}>
                      <FormattedMessage id="layout.header.organizationsetting.testops_homepage" />
                    </Typography>
                  )}
                  secondary={(
                    <FormattedMessage id="layout.header.organizationsetting.testops_homepage_detail" />
                  )}
                />
              </MenuItem>
            </>
          )}
        </div>
      </MenuListDecorator>
    </div>
  );
};

export default OrganizationSettingMenu;
